import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import maplibregl, {
  type GeoJSONSource,
  type LngLatBoundsLike,
  type MapGeoJSONFeature,
  type MapLayerMouseEvent,
  type Map as MapLibreMap,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { AlertTriangle } from "lucide-react";

import { ageColorExpression } from "@/listings/listing.freshness.ts";
import { ageInDays } from "@/listings/listing.format.ts";
import { loadBasemapStyle } from "@/map/basemap.ts";
import {
  compactPrice,
  displayedLngLat,
  hashId,
  toFeatureCollection,
} from "@/listings/listing.filter.ts";
import { circlePolygon, distanceMeters, eastOf, type LngLat } from "@/map/geo.ts";
import {
  addPillImages,
  ensureOpenPillImage,
  ensurePillImages,
  PILL_HIT_HALF_H,
  pillHitHalfWidth,
  pillImageExpression,
  TRACKED,
} from "@/map/pill-icons.ts";
import type { ResolvedTheme } from "@/theme/theme.ts";
import type { BikewayCollection } from "@/bikeways/bikeway.model.ts";
import type { DistrictCollection, DistrictSet } from "@/districts/district.model.ts";
import type { Listing } from "@housing/shared";

export const SRC_LISTINGS = "listings";
const SRC_DISTRICTS = "districts";
const SRC_DRAFT = "draft-circle";
const SRC_BIKEWAYS = "bikeways";
/** Everything this app puts on the map, as opposed to the basemap under it. */
const APP_SOURCES = new Set<string>([SRC_LISTINGS, SRC_DISTRICTS, SRC_DRAFT, SRC_BIKEWAYS]);

const FONT_REGULAR = ["Noto Sans Regular"];
const FONT_BOLD = ["Noto Sans Bold"];

/**
 * The cluster layers, the one part of listing interaction that still goes
 * through queryRenderedFeatures — aggregates only exist inside the source, so
 * there is nothing in props to hit-test against. Every query against them is
 * wrapped in try/catch: maplibre#8064 can leave a tile with a corrupt feature
 * index whose every query throws, and losing one cluster click beats losing
 * the whole click handler. Listing hits never query at all — see listingAt.
 */
const CLUSTER_LAYERS = ["clusters", "cluster-halo"];

/**
 * Every layer bound to the listings source, in draw order.
 *
 * This list has to be exhaustive, and the failure is quiet if it is not: the
 * cluster toggle tears these down before removing the source, and MapLibre
 * refuses to drop a source that any layer still references. One layer missing
 * here and the whole re-install aborts, leaving the map on its old clustering
 * until the page is reloaded. Adding a layer above means adding it here.
 */
const LISTING_LAYERS = [
  "cluster-halo",
  "clusters",
  "cluster-count",
  "listing-ring-dot",
  "listing-dots",
  "listing-pills",
  "listing-pill-selected",
];

/**
 * Zoom bands. Clusters live at or below CLUSTER_MAX_ZOOM; individual dots take
 * over from there; price pills replace the dots at PILL_MIN_ZOOM. Clusters and
 * pills therefore never share a zoom, which is the only reliable way to stop
 * them overlapping (see the clusterMaxZoom note below).
 *
 * The whole band moved two levels out, so the price is legible from a view that
 * still holds a few neighbourhoods rather than one that holds a few blocks. The
 * pills are far too dense to all draw at that scale — which is the point:
 * `symbol-sort-key` culls by age, so what survives the collision pass is the
 * freshest price in each patch of map.
 */
const CLUSTER_MAX_ZOOM = 12;

/**
 * How long a clicked aggregate takes to fade before the camera moves.
 *
 * The camera used to start immediately, and the bubble only vanished later, when
 * the source re-clustered at the new zoom — so the count appeared to hang in the
 * air over an already-moving map and then blink out. Fading first makes the
 * bubble the thing you dismissed rather than debris the zoom left behind.
 */
const CLUSTER_FADE_MS = 180;

/**
 * Tap targets sized for whatever is doing the pointing.
 *
 * A mouse lands where it is aimed. A fingertip covers something like 40–45px and
 * lands where it roughly meant to, so on touch the invisible target over each
 * listing grows to match — otherwise the only way to hit the pin you want is to
 * keep zooming until it is big enough, which is a workaround, not a design.
 */
const COARSE_POINTER =
  typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
/** Radius, not diameter — 24 here is a 48px target, just over the 44px guideline. */
const HIT_RADIUS = COARSE_POINTER ? 24 : 14;

/**
 * Listings posted today that haven't been opened flash by slowly dimming and
 * recovering. This replaced an expanding ripple ring: the ring was its own
 * layer and read as a different object from the pill it sat under, where
 * breathing the mark itself keeps the "posted today" signal on the thing that
 * carries it. It stops the moment the listing is opened — `seen` flips on the
 * feature and the opacity expression falls back to the base.
 */
const FLASH_MS = 2400;
/** The bottom of the dip — deep enough to read as a flash across a full
 *  viewport, shallow enough that the price never becomes unreadable. */
const FLASH_MIN = 0.35;
/** How often the flash retargets its paint properties — see the loop. */
const FLASH_STEP_MS = 150;

/**
 * `value`, unless this aggregate is being dismissed or is standing on top of a
 * hand-placed pin — then 0.
 *
 * `suppressed` is how the pin gets right of way. Cluster bubbles are a `circle`
 * layer and circles take no part in MapLibre's symbol collision, so nothing in
 * the style can make an aggregate yield; the only way to guarantee they never
 * overlap is to take the bubble out. See pins/ReactorPin.tsx, which owns the
 * hit-testing and sets this state.
 */
const fadeOut = (value: number): unknown[] => [
  "case",
  [
    "any",
    ["boolean", ["feature-state", "dismissing"], false],
    ["boolean", ["feature-state", "suppressed"], false],
  ],
  0,
  value,
];
const PILL_MIN_ZOOM = 13;
/**
 * Where the dot ramps finish growing — just short of the zoom that retires them,
 * so a dot is at full size the instant before its pill replaces it and the swap
 * doesn't read as the mark shrinking.
 */
const DOT_MAX_ZOOM = PILL_MIN_ZOOM - 0.6;

const MARINE = "#3adcc8";
const INK = "#04070a";

/**
 * The two cluster colours that cannot be fixed: a near-black bubble with pale
 * digits disappears into a light basemap. Everything else in the aggregate —
 * the halo and the ring — is the age ramp, which reads on both.
 *
 * Applied from one place so the two paths that need it agree: the layer install
 * (which re-runs on every style swap) and the theme toggle itself.
 */
const CLUSTER_THEME = {
  dark: { fill: "#0b141b", count: "#e8f0f5", halo: "#dbe7ef" },
  light: { fill: "#f7fafc", count: "#0b141b", halo: "#0b141b" },
} as const;

/**
 * The "not opened yet" ring, which is the one mark on the map that has to fight
 * the basemap rather than sit on a fill of its own.
 *
 * One colour per theme, not the listing's own. It used to be drawn in the pill's
 * age colour, which meant its visibility was whatever that colour happened to
 * give it — a slate ring on a pale map, a pale ring on a dark one — so on half
 * the ramp the ornament that is supposed to say "look here" was the hardest
 * thing on the pin to see. A single high-contrast outline says the same thing at
 * the same volume everywhere, and leaves the age entirely to the fill.
 */
const UNOPENED_RING = { dark: "#dbe7ef", light: "#0b141b" } as const;

/**
 * District names, which sit on the basemap rather than on a fill of their own
 * and so have to be told which way round the world is.
 *
 * These were one hardcoded pair — slate lettering inside a near-black halo —
 * which is right at night and close to unreadable by day: a black outline
 * around pale letters on a cream map leaves the halo doing the reading. The
 * selected colour needed splitting for the same reason; marine is a light key
 * and all but vanishes against paper.
 */
const DISTRICT_LABEL = {
  dark: { text: "#7f97a5", selected: "#3adcc8", halo: "#04070a" },
  light: { text: "#4a5a66", selected: "#0f7d70", halo: "#f7f5f1" },
} as const;

/** `text-color` for the district labels, given what is selected. */
function districtLabelColor(theme: ResolvedTheme, selected: unknown): unknown {
  const c = DISTRICT_LABEL[theme];
  return ["case", selected, c.selected, c.text];
}

/**
 * The bike-infrastructure overlay. Green — the colour paint on the actual
 * street is — and one per theme, since a green that sits comfortably on the
 * nocturne basemap is fluorescent on paper. Kept well away from marine so the
 * district tools and this overlay never read as the same system.
 *
 * Both layers share the colour; the *dash* carries the distinction. A solid
 * line is something physically separated from traffic, a dashed one is paint
 * on the roadway — the dashes literally are the paint.
 */
const BIKEWAY = { dark: "#67c979", light: "#1f8a44" } as const;
const BIKEWAY_LAYERS = ["bikeway-path", "bikeway-lane"] as const;

/** Which basemap layers this map has hidden, so only those get put back. */
const hiddenLabels = new WeakMap<MapLibreMap, string[]>();

/**
 * Every word the basemap draws, taken down while districts are being picked.
 *
 * Picking is a modal act — the only question on screen is which of these areas
 * you want — and the cartography answers a different one at the same volume.
 * The neighbourhood names were the worst of it, since half of them name the very
 * polygon underneath and nothing distinguishes the copy you can click from the
 * copy you can't, but street names and shields compete just as hard for a
 * surface whose whole content is now a dozen large hit targets.
 *
 * Selected by source rather than by id: the two upstream styles disagree about
 * nearly every layer name (see `basemap.ts`), and a list of theirs is a list
 * that rots. Anything drawn from one of our own sources — prices, counts, the
 * district names themselves — stays, because all of it is still actionable.
 *
 * Only layers this actually hid are restored, so a layer the style ships
 * switched off is not switched on by closing the picker.
 */
function setBasemapLabels(map: MapLibreMap, visible: boolean): void {
  if (visible) {
    for (const id of hiddenLabels.get(map) ?? []) {
      if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", "visible");
    }
    hiddenLabels.delete(map);
    return;
  }

  const hidden: string[] = [];
  for (const layer of map.getStyle().layers) {
    if (layer.type !== "symbol") continue;
    if (APP_SOURCES.has(layer.source)) continue;
    if (map.getLayoutProperty(layer.id, "visibility") === "none") continue;
    map.setLayoutProperty(layer.id, "visibility", "none");
    hidden.push(layer.id);
  }
  hiddenLabels.set(map, hidden);
}

/**
 * A dot's fill: the shortlist colour if it is on the list, otherwise its age.
 *
 * The one place a mark is allowed to take the colour channel over. It is the
 * only mark the viewer makes deliberately, and by the time something is on the
 * shortlist its age has already been read — whereas seen/unseen is bookkeeping
 * nobody asked for and so gets an ornament instead.
 */
const dotColor = (): unknown[] => ["case", ["get", "tracked"], TRACKED, ageColorExpression()];

/**
 * Which pill wins the space when two want it. Lower sorts first.
 *
 * Age is the default tie-break — at a zoom that can't fit every price, the
 * freshest one is the one worth showing. A shortlisted listing jumps the whole
 * queue regardless: the entire reason to mark one is to find it again later, and
 * a mark that the collision pass is free to hide does not do that. The open card
 * outranks even that, so the pill you are reading never blinks out from under
 * you when the map moves.
 */
function pillSortKey(selectedId: string | null, cluster: boolean): unknown {
  const base: unknown = ["case", ["get", "tracked"], -1, ["get", "ageDays"]];
  const key: unknown = selectedId ? ["case", ["==", ["get", "id"], selectedId], -2, base] : base;
  // Grouped, a lower key places first and survives the collision pass.
  // Ungrouped, nothing is culled and the key decides draw order instead —
  // lower draws first, i.e. underneath — so the same rubric flips sign to put
  // the freshest / tracked / open pill on top of a stack rather than under it.
  return cluster ? key : ["*", -1, key];
}

/**
 * The open listing's pill is drawn by its own layer above `listing-pills`, and
 * these two filters split one feature between them.
 *
 * A separate layer is the only real "bring to front". Within one symbol layer
 * MapLibre draws every icon in one pass and every text in another, so whatever
 * the sort order says, an overlapping neighbour's price is free to print across
 * the top pill's background. A second layer draws after both of the first's
 * passes — background and price together, on top of everything.
 *
 * Listing ids are never empty, so `""` is the match-nothing resting state. The
 * cluster guard runs first in each filter (and `all` short-circuits), keeping
 * the id comparison off aggregate features that have no id at all.
 */
const pillBaseFilter = (selectedId: string | null): unknown[] => [
  "all",
  ["!", ["has", "point_count"]],
  ["!=", ["get", "id"], selectedId ?? ""],
];
const pillTopFilter = (selectedId: string | null): unknown[] => [
  "all",
  ["!", ["has", "point_count"]],
  ["==", ["get", "id"], selectedId ?? ""],
];

/** The unopened ring on a dot, which is a real stroke rather than a baked image. */
function ringDotStroke(theme: ResolvedTheme, selectedId: string | null): unknown {
  const ring = UNOPENED_RING[theme];
  return selectedId ? ["case", ["==", ["get", "id"], selectedId], MARINE, ring] : ring;
}

/**
 * Everything whose colour comes from the theme rather than from a listing.
 *
 * Applied from one place so the two paths that need it agree: the layer install
 * (which re-runs on every style swap) and the theme toggle itself.
 */
function applyMapTheme(map: MapLibreMap, theme: ResolvedTheme, selectedId: string | null): void {
  addPillImages(map, { marine: MARINE, outline: UNOPENED_RING[theme] });
  if (map.getLayer("listing-ring-dot")) {
    map.setPaintProperty(
      "listing-ring-dot",
      "circle-stroke-color",
      ringDotStroke(theme, selectedId) as never,
    );
  }
  // Only the halo here: the lettering also encodes which districts are picked,
  // and that is owned by `syncSources`, which re-runs on every swap anyway.
  if (map.getLayer("districts-label")) {
    map.setPaintProperty("districts-label", "text-halo-color", DISTRICT_LABEL[theme].halo);
  }
  for (const id of BIKEWAY_LAYERS) {
    if (map.getLayer(id)) map.setPaintProperty(id, "line-color", BIKEWAY[theme]);
  }
  const c = CLUSTER_THEME[theme];
  if (map.getLayer("clusters")) map.setPaintProperty("clusters", "circle-color", c.fill);
  if (map.getLayer("cluster-count")) map.setPaintProperty("cluster-count", "text-color", c.count);
  // The backing disc opposes the bubble rather than matching it: a pale wash
  // behind a dark bubble, a dark one behind a pale bubble. Tinting it by age
  // instead — as it used to be — left it nearly invisible on half the ramp,
  // since a slate halo at low opacity has nothing to separate it from the map.
  // Age is still carried by the ring, which is the higher-contrast edge anyway.
  if (map.getLayer("cluster-halo")) map.setPaintProperty("cluster-halo", "circle-color", c.halo);
}

export interface DraftCircle {
  center: LngLat;
  radiusM: number;
}

export interface MapCanvasProps {
  listings: Listing[];
  districts: DistrictCollection | null;
  /** Drives which basemap key is loaded; they move together by design. */
  theme: ResolvedTheme;
  initialCamera: { center: LngLat; zoom: number };
  onCameraChange: (camera: { center: LngLat; zoom: number }) => void;
  /** Fires once the map instance exists and its first style has loaded. */
  onReady?: () => void;
  /** Open the detail card for a listing (rendered by the parent, outside the map). */
  onSelectListing: (listing: Listing | null) => void;
  /** Bike-infrastructure lines; null until their lazy fetch lands. */
  bikeways: BikewayCollection | null;
  showBikeways: boolean;
  /** Ids already opened. Changes the pin's form, never its colour. */
  seen: ReadonlySet<string>;
  /** Ids on the shortlist. The one mark that *does* take over the colour. */
  tracked: ReadonlySet<string>;
  selectedId: string | null;

  /** Group nearby listings into counted bubbles when zoomed out. */
  cluster: boolean;

  /** Radius tool: non-null puts the map into "drawing" mode. */
  draftCircle: DraftCircle | null;
  radiusToolActive: boolean;
  onDraftCircleChange: (c: DraftCircle) => void;

  /** District tool: non-null set puts polygons on the map and makes them clickable. */
  districtToolSet: DistrictSet | null;
  draftDistrictIds: string[];
  onToggleDistrict: (id: string) => void;
}

/** Imperative handle so the panel can fly the map somewhere. */
export interface MapHandle {
  fitBounds: (bounds: LngLatBoundsLike, padding?: number) => void;
  flyTo: (center: LngLat, zoom: number) => void;
  /**
   * Same destination as `flyTo`, arrived at instantly.
   *
   * For opening on a shared link, where there is no previous view to animate
   * away from — and, more to the point, where the detail popup mounts in the
   * same tick and runs its own `easeTo` to nudge itself on screen. That nudge
   * keeps whatever zoom it finds, so an animated fly is still mid-flight when
   * it gets sampled and the arrival lands short.
   */
  jumpTo: (center: LngLat, zoom: number) => void;
  /**
   * Slide a point to a given fraction of the viewport height (0 = top edge,
   * 0.5 = centre) without changing zoom — used to lift a selected listing clear
   * of the mobile detail sheet.
   */
  revealAt: (center: LngLat, verticalFraction: number) => void;
  /** The live instance, for overlays that anchor themselves to the map. */
  map: MapLibreMap | null;
}

export function MapCanvas({
  listings,
  districts,
  bikeways,
  showBikeways,
  theme,
  initialCamera,
  onCameraChange,
  onReady,
  onSelectListing,
  seen,
  tracked,
  selectedId,
  cluster,
  draftCircle,
  radiusToolActive,
  onDraftCircleChange,
  districtToolSet,
  draftDistrictIds,
  onToggleDistrict,
  handleRef,
}: MapCanvasProps & { handleRef?: React.RefObject<MapHandle | null> }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const readyRef = useRef(false);
  const hoveredRef = useRef<number | string | null>(null);
  const flashRaf = useRef(0);
  /** Whether any visible listing is fresh-and-unseen — set by syncSources. */
  const hasFreshRef = useRef(true);
  /** True while the flash has painted anything short of full opacity. */
  const flashDirty = useRef(false);
  /** installLayers has no deps by design, so it reads the live theme from here. */
  const themeRef = useRef(theme);
  themeRef.current = theme;
  // Read by the layer install, which is a `[]` callback and so cannot close over
  // either: a basemap swap must rebuild the map as it is now, not as it was.
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const centerMarkerRef = useRef<maplibregl.Marker | null>(null);
  const handleMarkerRef = useRef<maplibregl.Marker | null>(null);
  const onReadyRef = useRef(onReady);
  /** Set only when the map never got built — see the mount effect's catch. */
  const [initError, setInitError] = useState<string | null>(null);

  // Latest props, read from inside long-lived map event handlers. Registering the
  // handlers once and reading through refs avoids tearing down listeners (and
  // losing in-flight interactions) on every render. Synced in a layout effect
  // rather than during render — mutating a ref while rendering is a rules-of-
  // React violation that would make the compiler skip this component, and
  // layout effects land before any user event can read a stale value.
  const props = useRef({
    listings,
    seen,
    tracked,
    districts,
    bikeways,
    showBikeways,
    cluster,
    onSelectListing,
    radiusToolActive,
    districtToolSet,
    draftDistrictIds,
    draftCircle,
    onDraftCircleChange,
    onToggleDistrict,
  });
  useLayoutEffect(() => {
    props.current = {
      listings,
      seen,
      tracked,
      districts,
      bikeways,
      showBikeways,
      cluster,
      onSelectListing,
      radiusToolActive,
      districtToolSet,
      draftDistrictIds,
      draftCircle,
      onDraftCircleChange,
      onToggleDistrict,
    };
    onReadyRef.current = onReady;
  });

  /**
   * Push every app-owned source's data from the current props. One function
   * rather than per-source effects because a basemap swap wipes all three at
   * once and has to refill them together — the effects below just call this.
   */
  const syncSources = useCallback((map: MapLibreMap) => {
    const p = props.current;

    const fc = toFeatureCollection(p.listings, { seen: p.seen, tracked: p.tracked });
    // Images before data, always: a tile built while an image it needs is
    // still missing can come out of the worker with a corrupt feature index
    // (maplibre#8064) — see listingAt for what that used to cost.
    ensurePillImages(map, fc.features);
    // Whether anything on the map is worth flashing — lets the animation loop
    // stop touching paint entirely when nothing qualifies.
    hasFreshRef.current = fc.features.some((f) => f.properties.ageDays < 1 && !f.properties.seen);
    (map.getSource(SRC_LISTINGS) as GeoJSONSource | undefined)?.setData(fc);

    const districtData: GeoJSON.FeatureCollection =
      p.districtToolSet && p.districts
        ? {
            type: "FeatureCollection",
            features: p.districts.features.filter((f) => f.properties.set === p.districtToolSet),
          }
        : { type: "FeatureCollection", features: [] };
    (map.getSource(SRC_DISTRICTS) as GeoJSONSource | undefined)?.setData(districtData);

    (map.getSource(SRC_BIKEWAYS) as GeoJSONSource | undefined)?.setData(
      p.bikeways ?? { type: "FeatureCollection", features: [] },
    );

    (map.getSource(SRC_DRAFT) as GeoJSONSource | undefined)?.setData(
      p.draftCircle
        ? {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                properties: {},
                geometry: circlePolygon(p.draftCircle.center, p.draftCircle.radiusM),
              },
            ],
          }
        : { type: "FeatureCollection", features: [] },
    );

    if (map.getLayer("districts-fill-selected")) {
      // An empty selection needs no sentinel: `in` over an empty list is
      // already always false.
      const selected = ["in", ["get", "id"], ["literal", p.draftDistrictIds]];
      map.setFilter("districts-fill-selected", selected as never);
      map.setPaintProperty("districts-line", "line-width", ["case", selected, 2.2, 1] as never);
      map.setPaintProperty("districts-line", "line-opacity", [
        "case",
        selected,
        0.95,
        0.3,
      ] as never);
      map.setPaintProperty(
        "districts-label",
        "text-color",
        districtLabelColor(themeRef.current, selected) as never,
      );
    }
  }, []);

  /** Add every app-owned source and layer. Re-run after each basemap swap. */
  const installLayers = useCallback((map: MapLibreMap) => {
    addPillImages(map, { marine: MARINE, outline: UNOPENED_RING[themeRef.current] });

    if (!map.getSource(SRC_BIKEWAYS)) {
      map.addSource(SRC_BIKEWAYS, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }
    if (!map.getSource(SRC_DISTRICTS)) {
      map.addSource(SRC_DISTRICTS, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }
    if (!map.getSource(SRC_DRAFT)) {
      map.addSource(SRC_DRAFT, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }
    if (!map.getSource(SRC_LISTINGS)) {
      map.addSource(SRC_LISTINGS, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
        cluster: props.current.cluster,
        clusterRadius: 52,
        // Past this zoom every point stands alone, which is also where the price
        // pills take over from the dots.
        // Clustering stops one zoom below where the pills start. Cluster bubbles
        // are a `circle` layer, and circles take no part in MapLibre's symbol
        // collision — a pill will happily draw straight through one. Keeping the
        // two in disjoint zoom bands is what actually prevents that; there's no
        // paint-order or padding trick that would.
        clusterMaxZoom: CLUSTER_MAX_ZOOM,
        // Carry the freshest listing in each cluster up to the cluster itself, so
        // an aggregate containing something posted today still glows amber.
        clusterProperties: { minAge: ["min", ["get", "ageDays"]] },
      });
    }

    // ── bikeways (first in, so beneath every other app layer) ───────────────
    if (!map.getLayer("bikeway-path")) {
      const width: unknown[] = ["interpolate", ["linear"], ["zoom"], 10, 0.8, 14, 1.8, 17, 3.5];
      const visibility = props.current.showBikeways ? "visible" : "none";
      map.addLayer({
        id: "bikeway-path",
        type: "line",
        source: SRC_BIKEWAYS,
        filter: ["==", ["get", "kind"], "path"],
        minzoom: 10,
        layout: { "line-cap": "round", "line-join": "round", visibility },
        paint: {
          "line-color": BIKEWAY[themeRef.current],
          "line-width": width as never,
          "line-opacity": 0.8,
        },
      });
      map.addLayer({
        id: "bikeway-lane",
        type: "line",
        source: SRC_BIKEWAYS,
        filter: ["==", ["get", "kind"], "lane"],
        minzoom: 10,
        // Default (butt) caps: round caps extend each dash and close the gaps.
        layout: { "line-join": "round", visibility },
        paint: {
          "line-color": BIKEWAY[themeRef.current],
          "line-width": width as never,
          "line-opacity": 0.65,
          "line-dasharray": [2.4, 1.8],
        },
      });
    }

    // ── districts (below listings, above the basemap) ───────────────────────
    if (!map.getLayer("districts-fill")) {
      map.addLayer({
        id: "districts-fill",
        type: "fill",
        source: SRC_DISTRICTS,
        paint: { "fill-color": MARINE, "fill-opacity": 0.05 },
      });
      map.addLayer({
        id: "districts-fill-selected",
        type: "fill",
        source: SRC_DISTRICTS,
        // Matches nothing until a selection arrives. An empty `in` list is
        // always false, which says that outright rather than relying on a
        // sentinel value that no real slug happens to equal.
        filter: ["in", ["get", "id"], ["literal", []]],
        paint: { "fill-color": MARINE, "fill-opacity": 0.16 },
      });
      map.addLayer({
        id: "districts-line",
        type: "line",
        source: SRC_DISTRICTS,
        paint: {
          "line-color": MARINE,
          "line-width": ["case", ["boolean", ["feature-state", "selected"], false], 2, 1],
          "line-opacity": ["case", ["boolean", ["feature-state", "selected"], false], 0.95, 0.32],
        },
      });
      map.addLayer({
        id: "districts-label",
        type: "symbol",
        source: SRC_DISTRICTS,
        layout: {
          // Regular rather than bold: uppercase at this tracking is already
          // heavy, and the halo carries legibility over a busy basemap.
          "text-field": ["get", "name"],
          "text-font": FONT_REGULAR,
          "text-size": 11,
          "text-transform": "uppercase",
          "text-letter-spacing": 0.08,
          "text-allow-overlap": false,
        },
        paint: {
          "text-color": districtLabelColor(themeRef.current, [
            "boolean",
            ["feature-state", "selected"],
            false,
          ]) as never,
          "text-halo-color": DISTRICT_LABEL[themeRef.current].halo,
          "text-halo-width": 1.6,
        },
      });
    }

    // ── the draft radius circle ─────────────────────────────────────────────
    if (!map.getLayer("draft-fill")) {
      map.addLayer({
        id: "draft-fill",
        type: "fill",
        source: SRC_DRAFT,
        paint: { "fill-color": MARINE, "fill-opacity": 0.08 },
      });
      map.addLayer({
        id: "draft-line",
        type: "line",
        source: SRC_DRAFT,
        paint: { "line-color": MARINE, "line-width": 1.6, "line-dasharray": [3, 2.5] },
      });
    }

    // ── listings ────────────────────────────────────────────────────────────
    if (!map.getLayer("cluster-halo")) {
      const clusterRadius: unknown[] = [
        "interpolate",
        ["linear"],
        ["get", "point_count"],
        2,
        15,
        10,
        20,
        50,
        27,
        200,
        35,
        800,
        46,
      ];

      // A soft outer disc: the aggregate reads as "many" before you count it.
      map.addLayer({
        id: "cluster-halo",
        type: "circle",
        source: SRC_LISTINGS,
        filter: ["has", "point_count"],
        paint: {
          "circle-color": ageColorExpression("minAge") as never,
          "circle-opacity": fadeOut(0.24) as never,
          "circle-opacity-transition": { duration: CLUSTER_FADE_MS },
          "circle-radius": ["*", clusterRadius as never, 1.7],
        },
      });
      map.addLayer({
        id: "clusters",
        type: "circle",
        source: SRC_LISTINGS,
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#0b141b",
          "circle-opacity": fadeOut(0.95) as never,
          "circle-opacity-transition": { duration: CLUSTER_FADE_MS },
          "circle-radius": clusterRadius as never,
          "circle-stroke-width": 1.8,
          "circle-stroke-color": ageColorExpression("minAge") as never,
          "circle-stroke-opacity": fadeOut(1) as never,
          "circle-stroke-opacity-transition": { duration: CLUSTER_FADE_MS },
        },
      });
      map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: SRC_LISTINGS,
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-font": FONT_BOLD,
          "text-size": ["interpolate", ["linear"], ["get", "point_count"], 2, 13, 200, 17],
          "text-allow-overlap": true,
        },
        paint: {
          "text-color": "#e8f0f5",
          "text-opacity": fadeOut(1) as never,
          "text-opacity-transition": { duration: CLUSTER_FADE_MS },
        },
      });

      // Individual listings: a dot that grows with zoom, plus a price pill once
      // there's room for one.
      // A ring around anything NOT yet opened — one fixed colour against the
      // basemap, and marine on whichever one is open right now. The mark inside
      // is untouched either way, so the age it encodes never shifts.
      map.addLayer({
        id: "listing-ring-dot",
        type: "circle",
        source: SRC_LISTINGS,
        // The cluster guard runs first: aggregates carry no `seen`, and negating
        // the null it reads back is a per-feature console warning.
        filter: ["all", ["!", ["has", "point_count"]], ["!", ["get", "seen"]]],
        maxzoom: PILL_MIN_ZOOM,
        paint: {
          "circle-color": "rgba(0,0,0,0)",
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 6, 11, 8, DOT_MAX_ZOOM, 11],
          "circle-stroke-width": 2,
          "circle-stroke-color": ringDotStroke(themeRef.current, selectedIdRef.current) as never,
        },
      });

      map.addLayer({
        id: "listing-dots",
        type: "circle",
        source: SRC_LISTINGS,
        filter: ["!", ["has", "point_count"]],
        maxzoom: PILL_MIN_ZOOM,
        paint: {
          "circle-color": dotColor() as never,
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 2.5, 11, 4.5, DOT_MAX_ZOOM, 7],
          "circle-stroke-width": ["case", ["boolean", ["feature-state", "hover"], false], 2.5, 1],
          "circle-stroke-color": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            "#ffffff",
            INK,
          ],
          "circle-opacity": 0.95,
        },
      });
      // There is no invisible hit-target layer any more: listing clicks are
      // resolved in screen space by `listingAt`, which grows small dots to a
      // tappable size without the map carrying an extra layer for it.

      map.addLayer({
        id: "listing-pills",
        type: "symbol",
        source: SRC_LISTINGS,
        filter: pillBaseFilter(selectedIdRef.current) as never,
        minzoom: PILL_MIN_ZOOM,
        layout: {
          // One baked image per pill — background, ring and price together
          // (see pill-icons.ts). The price used to be layer text stretched
          // over a nine-patch icon, and that split is what let an overlapped
          // pill's price print across the pill above it: a symbol layer
          // paints all icons in one pass and all texts in another, so no
          // ordering keeps a text under a neighbouring icon. Atomic images
          // make overlap clip like stacked cards, every visible pill keeps
          // its own price, and the whole face of the pill is a click target.
          "icon-image": pillImageExpression(selectedIdRef.current) as never,
          // With Group nearby OFF every pill draws, overlapping or not — the
          // promise of un-grouping is that no listing is hidden. Grouped,
          // collision culls whole pills and symbol-sort-key keeps the
          // freshest in each patch. No setters needed — the toggle reinstalls
          // these layers, so this re-evaluates on every flip.
          "icon-allow-overlap": !props.current.cluster,
          "icon-padding": 4,
          "symbol-sort-key": pillSortKey(selectedIdRef.current, props.current.cluster) as never,
        },
      });

      // The open listing's pill, alone in a layer above the rest so it draws
      // over any colliding neighbour — see pillTopFilter for why a sort key
      // can't do this. Overlap is always on here: the one pill that must
      // never be culled is the one being read.
      map.addLayer({
        id: "listing-pill-selected",
        type: "symbol",
        source: SRC_LISTINGS,
        filter: pillTopFilter(selectedIdRef.current) as never,
        minzoom: PILL_MIN_ZOOM,
        layout: {
          "icon-image": pillImageExpression(selectedIdRef.current) as never,
          "icon-allow-overlap": true,
        },
      });
    }

    // MapLibre has no time-based expression, so the flash is driven from rAF.
    // Re-arming here rather than once at mount matters: a theme swap re-installs
    // every layer, and without cancelling first each swap would leave another
    // loop running alongside the last, all fighting over the same property.
    // The layers above are installed with the dark palette baked in, so correct
    // them here — this runs on the initial build and again after every style
    // swap, which is exactly when they would otherwise revert.
    applyMapTheme(map, themeRef.current, selectedIdRef.current);
    // A swap brings the basemap's labels back with it, so re-hide them if the
    // picker is still open.
    setBasemapLabels(map, props.current.districtToolSet === null);

    cancelAnimationFrame(flashRaf.current);
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // With motion off the marks simply hold their base opacity — the age colour
    // already says "today", so nothing is lost but the movement.
    if (still) return;

    // Which marks flash: posted today, never opened. Both layers' filters have
    // already excluded clusters, so the properties are always present.
    const fresh: unknown[] = ["all", ["<", ["get", "ageDays"], 1], ["!", ["get", "seen"]]];
    const paint = (v: number) => {
      if (!map.getLayer("listing-pills") || !map.getLayer("listing-dots")) return;
      // The pill's background dims while its price stays printed at full
      // strength; below pill zoom the dot itself breathes instead.
      map.setPaintProperty("listing-pills", "icon-opacity", ["case", fresh, v, 1] as never);
      map.setPaintProperty("listing-dots", "circle-opacity", [
        "case",
        fresh,
        0.95 * v,
        0.95,
      ] as never);
    };
    // Stepped, not per-frame. These are data-driven paint properties, and
    // retargeting one forces the style to re-evaluate and re-upload paint
    // buffers for every listing tile — at 60Hz that was a visible drag on the
    // whole browser. ~7 steps a second is far below what a 2.4s fade needs,
    // and the matching transition duration makes the engine ease each step,
    // so the result is as smooth as the per-frame version at a fraction of
    // the work. The loop stays on rAF (not setInterval) so a hidden tab
    // pauses it entirely.
    map.setPaintProperty("listing-pills", "icon-opacity-transition", { duration: FLASH_STEP_MS });
    map.setPaintProperty("listing-dots", "circle-opacity-transition", { duration: FLASH_STEP_MS });

    let lastStep = 0;
    const tick = (t: number) => {
      flashRaf.current = requestAnimationFrame(tick);
      if (t - lastStep < FLASH_STEP_MS) return;
      lastStep = t;
      // Nothing fresh on the map: settle at full opacity once, then do no
      // paint work at all until syncSources says otherwise.
      if (!hasFreshRef.current) {
        if (flashDirty.current) {
          paint(1);
          flashDirty.current = false;
        }
        return;
      }
      const phase = (t % FLASH_MS) / FLASH_MS;
      // Cosine dip: full strength at both ends of the cycle, so the loop point
      // has no seam to show.
      const dip = (1 - Math.cos(2 * Math.PI * phase)) / 2;
      paint(1 - (1 - FLASH_MIN) * dip);
      flashDirty.current = true;
    };
    flashRaf.current = requestAnimationFrame(tick);
  }, []);

  // The loop holds a reference to the map, so it has to stop when this does.
  useEffect(() => () => cancelAnimationFrame(flashRaf.current), []);

  // Toggling the theme swaps the basemap asynchronously; the layers survive that
  // and keep their old colours, so recolour them directly rather than waiting for
  // a re-install that may not come.
  useEffect(() => {
    // No readiness dependency needed: the initial colouring is done by
    // installLayers, so this only has to catch the toggle.
    const map = mapRef.current;
    if (map) applyMapTheme(map, theme, selectedId);
  }, [theme, selectedId]);

  // ── map lifecycle ─────────────────────────────────────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container || mapRef.current) return;

    let cancelled = false;
    let map: MapLibreMap | null = null;

    void loadBasemapStyle(theme)
      .then((style) => {
        if (cancelled || !containerRef.current) return;
        map = new maplibregl.Map({
          container: containerRef.current,
          style,
          center: initialCamera.center,
          zoom: initialCamera.zoom,
          minZoom: 8,
          maxZoom: 18.5,
          // Attribution moves into the info button in MapControls: the corner of a
          // rental map is prime screen space, and a permanent line of vendor text
          // there earns nothing. The credits stay one tap away, which is what the
          // OSM/OpenMapTiles terms actually ask for.
          attributionControl: false,
          // Pitch/rotate add nothing to a 2-D listing map and make touch panning
          // easy to trigger by accident on a phone.
          pitchWithRotate: false,
          dragRotate: false,
          touchPitch: false,
        });
        map.touchZoomRotate.disableRotation();
        mapRef.current = map;
        // Dev-only handle: lets a REPL (or a browser-driving test) reach the map
        // without the app having to expose it in the component tree. Stripped from
        // production builds by the constant-folded import.meta.env.DEV check.
        if (import.meta.env.DEV) (window as unknown as { __map?: MapLibreMap }).__map = map;

        // No MapLibre controls at all: every piece of map chrome is React (see
        // MapControls / ScaleBar) so it shares the app's theme and tooltips.

        // The upstream dark style references a couple of pattern images its sprite
        // doesn't ship; answer with a transparent pixel so the console stays clean.
        map.on("styleimagemissing", (e) => {
          if (!map!.hasImage(e.id)) {
            map!.addImage(e.id, { width: 1, height: 1, data: new Uint8Array(4) });
          }
        });

        map.on("load", () => {
          installLayers(map!);
          readyRef.current = true;
          syncSources(map!);
          // Kick a render with whatever the parent already has.
          map!.fire("app:ready");
          onReadyRef.current?.();
        });

        // setStyle() wipes every app-owned source and layer. Rebuild them as soon
        // as the incoming style is applied — detected by our own source going
        // missing, which (unlike isStyleLoaded()) doesn't also wait on tiles and
        // so can't strand the map on a slow network. styledata fires often; the
        // getSource check makes the common case a no-op.
        map.on("styledata", () => {
          if (!readyRef.current || map!.getSource(SRC_LISTINGS)) return;
          installLayers(map!);
          syncSources(map!);
          map!.fire("app:ready");
        });

        map.on("moveend", () => {
          const c = map!.getCenter();
          onCameraChange({ center: [c.lng, c.lat], zoom: map!.getZoom() });
        });

        wireInteractions(map);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Nothing was constructed, so there is no degraded map to fall back to —
        // the container would just sit there empty. Say so in place rather than
        // leaving a blank rectangle and one line in a console nobody has open.
        const reason = err instanceof Error ? err.message : String(err);
        console.error(`[map] could not initialise — ${reason}`, err);
        setInitError(reason);
      });

    return () => {
      cancelled = true;
      map?.remove();
      mapRef.current = null;
      readyRef.current = false;
    };
    // Deliberately mount-only: the map instance outlives prop changes, which are
    // pushed in through the effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Click / hover behaviour. Registered once; reads live props through the ref. */
  const wireInteractions = useCallback((map: MapLibreMap) => {
    const setHover = (id: number | string | null) => {
      if (hoveredRef.current !== null) {
        map.setFeatureState({ source: SRC_LISTINGS, id: hoveredRef.current }, { hover: false });
      }
      hoveredRef.current = id;
      if (id !== null) map.setFeatureState({ source: SRC_LISTINGS, id }, { hover: true });
    };

    /**
     * Which listing a screen point lands on, resolved by projecting the
     * visible set ourselves rather than asking the map.
     *
     * Deliberately NOT queryRenderedFeatures. maplibre#8064: a GeoJSON tile
     * whose symbols referenced a not-yet-added image can come out of the
     * worker with a corrupt feature index, and from then on *every* query
     * through that tile throws — which is an uncaught error per click and a
     * map where "some pills don't open". Everything a listing query could
     * tell us is already in props, so screen-space maths is both immune and
     * exact: at pill zoom the target is the pill's real rendered box, below
     * it a finger-sized circle around the dot. Overlapping pills resolve to
     * the top of the stack by the same rubric that draws them.
     */
    const listingAt = (point: MapLayerMouseEvent["point"]): Listing | null => {
      const pills = map.getZoom() >= PILL_MIN_ZOOM;
      let best: Listing | null = null;
      let bestKey = Infinity;
      for (const l of props.current.listings) {
        const pos = map.project(displayedLngLat(l));
        const dx = point.x - pos.x;
        const dy = point.y - pos.y;
        if (pills) {
          if (Math.abs(dx) > pillHitHalfWidth(compactPrice(l.price))) continue;
          if (Math.abs(dy) > PILL_HIT_HALF_H) continue;
        } else if (dx * dx + dy * dy > HIT_RADIUS * HIT_RADIUS) {
          continue;
        }
        const key =
          l.id === selectedIdRef.current
            ? -2
            : props.current.tracked.has(l.id)
              ? -1
              : ageInDays(l.postedAt);
        if (key < bestKey) {
          bestKey = key;
          best = l;
        }
      }
      return best;
    };

    /** The cluster under a point, if any — the one query left, made survivable. */
    const clusterAt = (point: MapLayerMouseEvent["point"]): MapGeoJSONFeature | undefined => {
      try {
        return map.queryRenderedFeatures(point, {
          layers: CLUSTER_LAYERS.filter((l) => map.getLayer(l)),
        })[0];
      } catch {
        // A corrupt tile (see listingAt) — this click loses its cluster, the
        // handler survives for everything else.
        return undefined;
      }
    };

    const expandCluster = (feature: MapGeoJSONFeature) => {
      const clusterId = feature.properties?.cluster_id as number | undefined;
      if (clusterId === undefined) return;
      const source = map.getSource(SRC_LISTINGS) as GeoJSONSource;

      // Start the fade now, and look the expansion zoom up while it runs — the
      // camera then waits on whichever finishes last, so the bubble is always
      // gone before the map moves rather than during or after.
      map.setFeatureState({ source: SRC_LISTINGS, id: clusterId }, { dismissing: true });
      const faded = new Promise<void>((resolve) => {
        window.setTimeout(resolve, CLUSTER_FADE_MS);
      });

      void Promise.all([source.getClusterExpansionZoom(clusterId), faded]).then(([zoom]) => {
        const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
        // A shade past the expansion zoom so the members land visibly apart
        // rather than exactly at the moment they stop merging.
        map.easeTo({
          center: [lng, lat],
          zoom: Math.min(zoom + 0.4, map.getMaxZoom()),
          duration: 620,
          essential: true,
        });
        // Re-clustering usually retires this id anyway, but at max zoom the
        // same aggregate can survive the move — and without this it would stay
        // invisible for good.
        map.once("moveend", () => {
          map.removeFeatureState({ source: SRC_LISTINGS, id: clusterId }, "dismissing");
        });
      });
    };

    // One mousemove drives cursor and hover for listings and clusters alike.
    // Clusters take precedence: a bubble hides its members, so a point inside
    // one belongs to the aggregate, not to an invisible listing under it.
    map.on("mousemove", (e) => {
      // While a tool owns the map, its own handlers own the cursor too.
      if (props.current.radiusToolActive || props.current.districtToolSet) return;
      if (clusterAt(e.point)) {
        if (hoveredRef.current !== null) setHover(null);
        map.getCanvas().style.cursor = "zoom-in";
        return;
      }
      const listing = listingAt(e.point);
      if (listing) {
        map.getCanvas().style.cursor = "pointer";
        const fid = hashId(listing.id);
        if (fid !== hoveredRef.current) setHover(fid);
        return;
      }
      if (hoveredRef.current !== null) setHover(null);
      map.getCanvas().style.cursor = "";
    });
    map.on("mouseout", () => {
      if (hoveredRef.current !== null) setHover(null);
    });

    map.on("click", "districts-fill", (e: MapLayerMouseEvent) => {
      if (!props.current.districtToolSet) return;
      const id = e.features?.[0]?.properties?.id as string | undefined;
      if (!id) return;
      e.originalEvent.stopPropagation();
      props.current.onToggleDistrict(id);
    });
    map.on("mouseenter", "districts-fill", () => {
      if (props.current.districtToolSet) map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "districts-fill", () => {
      map.getCanvas().style.cursor = "";
    });

    // One click handler for the whole map: tool, listing, cluster, then
    // dismiss — in that order, so precedence is explicit rather than an
    // accident of listener registration.
    map.on("click", (e) => {
      if (props.current.radiusToolActive) {
        const radiusM = props.current.draftCircle?.radiusM ?? defaultRadiusFor(map);
        props.current.onDraftCircleChange({ center: [e.lngLat.lng, e.lngLat.lat], radiusM });
        return;
      }
      if (props.current.districtToolSet) return;
      // Cluster before listing, as on hover: the bubble hides its members.
      const aggregate = clusterAt(e.point);
      if (aggregate) {
        expandCluster(aggregate);
        return;
      }
      const listing = listingAt(e.point);
      if (listing) {
        props.current.onSelectListing(listing);
        return;
      }
      // An empty patch of map dismisses the card.
      props.current.onSelectListing(null);
    });
  }, []);

  // ── push data + prop changes into the map ─────────────────────────────────

  /**
   * Run `fn` once the app's own sources exist, now or on the next ready event.
   *
   * The readiness test is deliberately "are my sources installed", NOT
   * `isStyleLoaded()`: that flag also tracks in-flight tiles and can sit false
   * indefinitely on a slow network, which would strand every deferred update
   * behind an `app:ready` that never fires again.
   */
  const whenReady = useCallback((fn: (map: MapLibreMap) => void) => {
    const map = mapRef.current;
    if (!map) return;
    if (readyRef.current && map.getSource(SRC_LISTINGS)) {
      fn(map);
      return;
    }
    const once = () => {
      map.off("app:ready", once);
      fn(map);
    };
    map.on("app:ready", once);
  }, []);

  // All source data flows through one path, so a basemap swap can replay it.
  useEffect(() => {
    whenReady(syncSources);
  }, [
    listings,
    seen,
    tracked,
    districts,
    bikeways,
    districtToolSet,
    draftDistrictIds,
    draftCircle,
    whenReady,
    syncSources,
  ]);

  // The bikeway overlay is a visibility flip, not a data change — the source
  // keeps its lines so switching it back on is instant.
  useEffect(() => {
    whenReady((map) => {
      for (const id of BIKEWAY_LAYERS) {
        if (map.getLayer(id)) {
          map.setLayoutProperty(id, "visibility", showBikeways ? "visible" : "none");
        }
      }
    });
  }, [showBikeways, whenReady]);

  // Clear the basemap's neighbourhood names out of the picker's way.
  useEffect(() => {
    whenReady((map) => setBasemapLabels(map, districtToolSet === null));
  }, [districtToolSet, whenReady]);

  // The draft circle's two drag handles are DOM markers, not style state.
  useEffect(() => {
    // Tear them down synchronously rather than behind whenReady, so applying an
    // area always clears the overlay even if the style is mid-swap.
    if (!draftCircle) {
      centerMarkerRef.current?.remove();
      handleMarkerRef.current?.remove();
      centerMarkerRef.current = null;
      handleMarkerRef.current = null;
    }

    whenReady((map) => {
      if (!draftCircle) return;

      if (!centerMarkerRef.current) {
        centerMarkerRef.current = new maplibregl.Marker({
          element: markerElement("center"),
          draggable: true,
        })
          .setLngLat(draftCircle.center)
          .addTo(map);
        centerMarkerRef.current.on("drag", () => {
          const { lng, lat } = centerMarkerRef.current!.getLngLat();
          const radiusM = props.current.draftCircle?.radiusM ?? 800;
          props.current.onDraftCircleChange({ center: [lng, lat], radiusM });
        });
      } else {
        // Don't fight the pointer: skip repositioning the handle being dragged.
        if (!centerMarkerRef.current.isDraggable() || !isDragging(centerMarkerRef.current)) {
          centerMarkerRef.current.setLngLat(draftCircle.center);
        }
      }

      const handlePos = eastOf(draftCircle.center, draftCircle.radiusM);
      if (!handleMarkerRef.current) {
        handleMarkerRef.current = new maplibregl.Marker({
          element: markerElement("handle"),
          draggable: true,
        })
          .setLngLat(handlePos)
          .addTo(map);
        handleMarkerRef.current.on("drag", () => {
          const { lng, lat } = handleMarkerRef.current!.getLngLat();
          const center = props.current.draftCircle?.center ?? draftCircle.center;
          const radiusM = Math.max(120, distanceMeters(center, [lng, lat]));
          props.current.onDraftCircleChange({ center, radiusM });
        });
      } else if (!isDragging(handleMarkerRef.current)) {
        handleMarkerRef.current.setLngLat(handlePos);
      }
    });
  }, [draftCircle, whenReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getContainer().classList.toggle("map-drawing", radiusToolActive);
  }, [radiusToolActive]);

  // Clustering is a GeoJSON *source* option, not a paint property, so there's no
  // setter for it — the source and every layer bound to it have to be rebuilt.
  // Cheap enough (one setData of an in-memory collection) and far less
  // disruptive than reloading the style.
  const clusterRef = useRef(cluster);
  useEffect(() => {
    if (clusterRef.current === cluster) return;
    clusterRef.current = cluster;
    whenReady((map) => {
      for (const id of LISTING_LAYERS) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      if (map.getSource(SRC_LISTINGS)) map.removeSource(SRC_LISTINGS);
      installLayers(map);
      syncSources(map);
    });
  }, [cluster, whenReady, installLayers, syncSources]);

  // Theme swap: keep the camera, re-apply layers via the styledata handler.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    void loadBasemapStyle(theme)
      .then((style) => {
        mapRef.current?.setStyle(style, { diff: false });
      })
      .catch((err: unknown) => {
        // Unlike the mount path this is survivable: the map is already up and
        // keeps the basemap it has. The palette is off by a theme until the next
        // switch retries (basemap.ts drops failures from its cache), so this is
        // worth a console line and not a panel over a working map.
        console.error(`[map] no ${theme} basemap; keeping the current one`, err);
      });
  }, [theme]);

  // Dim everything but the selected listing's neighbours? No — just keep the
  // selection legible by lifting it into the dedicated top layer.
  useEffect(() => {
    whenReady((map) => {
      if (!map.getLayer("listing-pills")) return;
      // The marine "open" pill is the one image syncSources can't have
      // pre-drawn; make it exist before any layer asks for it (maplibre#8064).
      const open = selectedId ? props.current.listings.find((l) => l.id === selectedId) : null;
      if (open) {
        ensureOpenPillImage(map, {
          ageDays: ageInDays(open.postedAt),
          tracked: props.current.tracked.has(open.id),
          label: compactPrice(open.price),
        });
      }
      map.setLayoutProperty(
        "listing-pills",
        "symbol-sort-key",
        pillSortKey(selectedId, props.current.cluster) as never,
      );
      // The open listing moves from the base pill layer to the one above it, so
      // its background and price both draw over any pill it collides with.
      map.setFilter("listing-pills", pillBaseFilter(selectedId) as never);
      map.setLayoutProperty(
        "listing-pills",
        "icon-image",
        pillImageExpression(selectedId) as never,
      );
      if (map.getLayer("listing-pill-selected")) {
        map.setFilter("listing-pill-selected", pillTopFilter(selectedId) as never);
        map.setLayoutProperty(
          "listing-pill-selected",
          "icon-image",
          pillImageExpression(selectedId) as never,
        );
      }
      // Selection recolours the ring rather than adding a second mark. The
      // filter widens to include the open listing, which keeps a ring even
      // though opening it is what retires its own. Cluster guard first, as at
      // install — aggregates have no `seen` to read.
      const wears = [
        "all",
        ["!", ["has", "point_count"]],
        selectedId
          ? ["any", ["!", ["get", "seen"]], ["==", ["get", "id"], selectedId]]
          : ["!", ["get", "seen"]],
      ];
      if (map.getLayer("listing-ring-dot")) {
        map.setFilter("listing-ring-dot", wears as never);
        map.setPaintProperty(
          "listing-ring-dot",
          "circle-stroke-color",
          ringDotStroke(themeRef.current, selectedId) as never,
        );
      }
    });
  }, [selectedId, whenReady]);

  // `mapReady` flips once so overlays anchored to the map instance re-render
  // after it exists; the handle itself is a stable mutable object.
  useEffect(() => {
    if (!handleRef) return;
    const handle: MapHandle = {
      fitBounds: (bounds, padding = 64) => {
        mapRef.current?.fitBounds(bounds, { padding, duration: 700, maxZoom: 15.5 });
      },
      flyTo: (center, zoom) => {
        mapRef.current?.flyTo({ center, zoom, duration: 700, essential: true });
      },
      jumpTo: (center, zoom) => {
        mapRef.current?.jumpTo({ center, zoom });
      },
      revealAt: (center, verticalFraction) => {
        const map = mapRef.current;
        if (!map) return;
        // MapLibre's `offset` places the target that many pixels from the
        // container centre; negative y lifts it toward the top of the screen.
        const dy = (verticalFraction - 0.5) * map.getContainer().clientHeight;
        map.easeTo({ center, offset: [0, dy], duration: 420, essential: true });
      },
      get map() {
        return mapRef.current;
      },
    };
    handleRef.current = handle;
  }, [handleRef]);

  // Inline rather than a utility class: maplibre-gl.css ships unlayered, and
  // unlayered rules beat Tailwind's `@layer utilities`, so `.maplibregl-map`'s
  // own `position: relative` would otherwise win and collapse the container.
  //
  // Children are safe here only because `initError` is set nothing-was-built:
  // once MapLibre owns this node, it is its canvas and not ours to render into.
  return (
    <div ref={containerRef} style={{ position: "absolute", inset: 0 }}>
      {initError && (
        <div className="grid absolute inset-0 place-items-center p-6" role="alert">
          <div className="console flex max-w-sm items-start gap-2.5 rounded-console p-3.5">
            <AlertTriangle className="mt-px size-4 shrink-0 text-alert" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-[13px] leading-snug font-medium text-chalk">
                The map couldn&rsquo;t load
              </p>
              <p className="mt-1.5 text-[12px] leading-relaxed text-chalk-dim">
                Its basemap comes from OpenFreeMap. Check your connection and reload — filters and
                listings are unaffected.
              </p>
              <p className="mt-2 font-mono text-[10.5px] leading-relaxed break-words text-chalk-mute">
                {initError}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** A radius that covers a sensible slice of what's currently on screen. */
function defaultRadiusFor(map: MapLibreMap): number {
  const bounds = map.getBounds();
  const across = distanceMeters(
    [bounds.getWest(), bounds.getSouth()],
    [bounds.getEast(), bounds.getSouth()],
  );
  return Math.round(Math.min(Math.max(across * 0.16, 250), 6000) / 50) * 50;
}

function isDragging(marker: maplibregl.Marker): boolean {
  return marker.getElement().classList.contains("is-dragging");
}

function markerElement(kind: "center" | "handle"): HTMLElement {
  const el = document.createElement("div");
  el.setAttribute("aria-hidden", "true");
  if (kind === "center") {
    el.style.cssText = `
      width:18px;height:18px;border-radius:9999px;cursor:move;
      background:${MARINE};box-shadow:0 0 0 3px ${INK},0 0 14px ${MARINE}99;`;
  } else {
    el.style.cssText = `
      width:22px;height:22px;border-radius:9999px;cursor:ew-resize;
      background:${INK};border:2px solid ${MARINE};
      box-shadow:0 0 12px ${MARINE}66;
      display:flex;align-items:center;justify-content:center;`;
    const inner = document.createElement("div");
    inner.style.cssText = `width:8px;height:2px;background:${MARINE};border-radius:2px;`;
    el.appendChild(inner);
  }
  // MapLibre doesn't expose "is this marker mid-drag", so mark it ourselves and
  // read it back when deciding whether to reposition from props.
  el.addEventListener("pointerdown", () => el.classList.add("is-dragging"));
  window.addEventListener("pointerup", () => el.classList.remove("is-dragging"));
  return el;
}
